/**
 * ============================================================
 * helpers.ts — Funcoes Auxiliares para Slices da Store
 * ============================================================
 *
 * Responsabilidade: Fornecer funcoes utilitarias reutilizaveis
 *   pelos slices do Zustand, como reordenacao de listas.
 *
 * Quando usar: Em qualquer slice que precise reordenar itens
 *   com campo `order` (colunas, cards do dashboard, etc.).
 * ============================================================
 */

/**
 * Reordena um array de itens que possuem campo `order` numerico.
 * Usado para drag-and-drop de colunas, cards e configuracoes.
 *
 * @param items - Array de itens com campo `order`
 * @param fromIndex - Indice de origem (posicao atual do item arrastado)
 * @param toIndex - Indice de destino (posicao onde o item foi solto)
 * @returns Novo array com campos `order` recalculados sequencialmente (0, 1, 2...)
 *
 * @example
 *   const reordered = reorderByIndex(columns, 0, 2);
 *   // Move a coluna da posicao 0 para a posicao 2
 */
export function reorderByIndex<T extends { order: number }>(
  items: T[],
  fromIndex: number,
  toIndex: number,
): T[] {
  const sorted = [...items].sort((a, b) => a.order - b.order);
  if (fromIndex < 0 || fromIndex >= sorted.length || toIndex < 0 || toIndex >= sorted.length) return sorted;
  const [moved] = sorted.splice(fromIndex, 1);
  if (!moved) return sorted;
  sorted.splice(toIndex, 0, moved);
  return sorted.map((item, i) => ({ ...item, order: i }));
}
